// ═══════════════════════════════════════════════════════════════════════════
// CareersPage
// Public careers page. Mirrors the light editorial style of PressPage and uses
// the global HomeNav (master logo + menu). The site footer is rendered globally
// from main.jsx, so this page must NOT render its own <SiteFooter>.
// Route: /careers
// ═══════════════════════════════════════════════════════════════════════════
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useBreakpoint } from '../hooks/useWindowWidth';
import HomeNav from '../components/nav/HomeNav';

const SITE_URL = 'https://luxuryweddingdirectory.com';
const GOLD     = '#C4A35A';
const GD       = 'var(--font-heading-primary)';
const NU       = 'var(--font-body)';
const CAREERS_EMAIL = 'careers@luxuryweddingdirectory.com';

const INK       = '#2a1f0e';
const INK_SOFT  = 'rgba(42,31,14,0.65)';
const INK_FAINT = 'rgba(42,31,14,0.45)';

const DISCIPLINES = [
  'Editorial', 'Sales', 'Partnerships', 'Content',
  'AI', 'Luxury Travel', 'Wedding Marketing',
];

const WHY = [
  {
    title: 'Work that is seen',
    body: 'Your work reaches couples planning some of the most considered weddings in the world, alongside the venues and brands that define the category.',
  },
  {
    title: 'Range and ownership',
    body: 'A small team with real ownership. You will shape products, standards, and relationships, not simply execute against a list.',
  },
  {
    title: 'Taste as a discipline',
    body: 'We treat editorial quality, design, and detail as the work itself. Standards are not decoration; they are the product.',
  },
];

function buildSchema() {
  return [{
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Careers at Luxury Wedding Directory',
    description: 'Join the platform shaping the future of luxury weddings, travel, fashion, editorial, and AI powered discovery.',
    url: `${SITE_URL}/careers`,
    publisher: {
      '@type': 'Organization',
      name: 'Luxury Wedding Directory',
      url: SITE_URL,
    },
  }];
}

export default function CareersPage({ onViewContact }) {
  const { isMobile } = useBreakpoint();
  const schemas = buildSchema();

  const sectionStyle = {
    maxWidth: 960,
    margin: '0 auto',
    padding: isMobile ? '56px 24px' : '72px 48px',
    borderBottom: '1px solid rgba(196,163,90,0.15)',
  };
  const eyebrow = {
    fontFamily: NU, fontSize: 10, fontWeight: 600,
    letterSpacing: '0.28em', textTransform: 'uppercase',
    color: GOLD, margin: '0 0 28px',
  };
  const sectionTitle = {
    fontFamily: GD, fontSize: isMobile ? 26 : 32, fontWeight: 400,
    color: INK, lineHeight: 1.2, margin: '0 0 24px',
  };
  const body = {
    fontFamily: NU, fontSize: 15, color: INK, fontWeight: 300,
    lineHeight: 1.85, margin: '0 0 20px',
  };

  return (
    <>
      <Helmet>
        <title>Careers | Luxury Wedding Directory</title>
        <meta
          name="description"
          content="Careers at Luxury Wedding Directory. We are always open to exceptional people in editorial, sales, partnerships, content, AI, luxury travel, and wedding marketing."
        />
        <link rel="canonical" href={`${SITE_URL}/careers`} />
        <meta property="og:title"       content="Careers | Luxury Wedding Directory" />
        <meta property="og:description" content="Join the platform shaping the future of luxury weddings, travel, fashion, editorial, and AI powered discovery." />
        <meta property="og:url"         content={`${SITE_URL}/careers`} />
        <meta property="og:type"        content="website" />
        {schemas.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>

      <div style={{ minHeight: '100vh', background: '#FAF8F4', fontFamily: NU }}>
        {/* Global navigation, master logo + menu */}
        <HomeNav lightScheme hasHero={false} sticky onToggleDark={() => {}} />

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <div style={{
          borderBottom: '1px solid rgba(196,163,90,0.2)',
          padding: isMobile ? '48px 24px 56px' : '64px 48px 72px',
          maxWidth: 960,
          margin: '0 auto',
        }}>
          <p style={eyebrow}>Careers</p>
          <h1 style={{
            fontFamily: GD, fontSize: isMobile ? 36 : 52, fontWeight: 400,
            color: INK, lineHeight: 1.15, letterSpacing: '-0.01em',
            margin: '0 0 24px', maxWidth: 720,
          }}>
            Careers at Luxury Wedding Directory
          </h1>
          <p style={{
            fontFamily: NU, fontSize: isMobile ? 16 : 18, color: INK_SOFT,
            lineHeight: 1.75, margin: '0 0 20px', maxWidth: 600, fontWeight: 300,
          }}>
            Join the platform shaping the future of luxury weddings, travel,
            fashion, editorial, and AI powered discovery.
          </p>
          <p style={{
            fontFamily: NU, fontSize: 14, color: INK_FAINT,
            lineHeight: 1.7, margin: 0, maxWidth: 600, fontWeight: 300,
          }}>
            Luxury Wedding Directory is published by 5 Star Weddings Ltd, the
            company behind a collection of luxury wedding, travel, editorial, and
            technology platforms serving a global audience.
          </p>
        </div>

        {/* ── Why Join Us ───────────────────────────────────────────────────── */}
        <section style={sectionStyle}>
          <p style={eyebrow}>Why Join Us</p>
          <p style={{ ...body, maxWidth: 640, marginBottom: 40 }}>
            We sit at the intersection of luxury media, technology, and the
            wedding industry. The people who thrive here care deeply about craft,
            taste, and the experience of the couples and brands we serve.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 28 : 40,
          }}>
            {WHY.map((w) => (
              <div key={w.title}>
                <h3 style={{
                  fontFamily: GD, fontSize: 19, fontWeight: 400,
                  color: INK, margin: '0 0 12px',
                }}>
                  {w.title}
                </h3>
                <p style={{
                  fontFamily: NU, fontSize: 14, color: INK_SOFT,
                  lineHeight: 1.8, margin: 0, fontWeight: 300,
                }}>
                  {w.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Who We're Looking For ─────────────────────────────────────────── */}
        <section style={sectionStyle}>
          <p style={eyebrow}>Who We're Looking For</p>
          <p style={{ ...body, maxWidth: 640, marginBottom: 32 }}>
            We hire for judgement, taste, and initiative ahead of titles. If you
            do exceptional work and care about the details others overlook, we
            would like to know you. We are especially drawn to people across
            these disciplines:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {DISCIPLINES.map((d) => (
              <span key={d} style={{
                fontFamily: NU, fontSize: 12, fontWeight: 600,
                letterSpacing: '0.08em', color: INK,
                padding: '9px 16px', borderRadius: 2,
                border: '1px solid rgba(196,163,90,0.4)',
                background: 'rgba(196,163,90,0.06)',
              }}>
                {d}
              </span>
            ))}
          </div>
        </section>

        {/* ── Current Opportunities ─────────────────────────────────────────── */}
        <section style={sectionStyle}>
          <p style={eyebrow}>Current Opportunities</p>
          <h2 style={sectionTitle}>An open invitation</h2>
          <p style={{ ...body, maxWidth: 640, margin: 0 }}>
            We are not advertising fixed roles at the moment, but we are always
            open to exceptional people in editorial, sales, partnerships, content,
            AI, luxury travel, and wedding marketing.
          </p>
        </section>

        {/* ── Send Your Details ─────────────────────────────────────────────── */}
        <section style={{ ...sectionStyle, borderBottom: 'none', paddingBottom: isMobile ? 80 : 110 }}>
          <p style={eyebrow}>Send Your Details</p>
          <h2 style={sectionTitle}>Introduce yourself</h2>
          <p style={{ ...body, maxWidth: 600, marginBottom: 32 }}>
            If your work belongs here, tell us what you do, what you have built,
            and where you would add value. A short note and a link to your work
            is the perfect place to begin.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
            <a
              href={`mailto:${CAREERS_EMAIL}?subject=Introduction`}
              style={{
                fontFamily: NU, fontSize: 12, fontWeight: 700,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                color: '#fff', background: INK,
                padding: '15px 34px', borderRadius: 2,
                textDecoration: 'none', display: 'inline-block',
              }}
            >
              Introduce Yourself
            </a>
            {onViewContact && (
              <button
                onClick={onViewContact}
                style={{
                  fontFamily: NU, fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.16em', textTransform: 'uppercase',
                  color: INK, background: 'none',
                  padding: '15px 30px', borderRadius: 2,
                  border: '1px solid rgba(42,31,14,0.3)', cursor: 'pointer',
                }}
              >
                Contact the Team
              </button>
            )}
          </div>
          <p style={{
            fontFamily: NU, fontSize: 13, color: INK_FAINT,
            margin: '20px 0 0', lineHeight: 1.6,
          }}>
            Or email us directly at{' '}
            <a href={`mailto:${CAREERS_EMAIL}`} style={{ color: GOLD, textDecoration: 'none' }}>
              {CAREERS_EMAIL}
            </a>
          </p>
        </section>
      </div>
    </>
  );
}
